import { useEffect, useState } from 'react';
import { doc, onSnapshot, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config.js';
import { useAuth } from './AuthProvider.jsx';
import { useTenant } from '../tenant/TenantProvider.jsx';
import { memberDoc, staffDoc } from '../lib/firestorePaths.js';
import LegalDocument from '../components/LegalDocument.jsx';
import { CURRENT_TERMS_VERSION, buildLegalTextPlain } from '../lib/legalText.js';

export function RequireTerms({ children }) {
  const { user, claims } = useAuth();
  const { gymId } = useTenant();
  const [accepted, setAccepted] = useState(null);
  const [docRef, setDocRef] = useState(null);

  useEffect(() => {
    if (!user || !gymId) { setAccepted(true); return; }
    // Check the right doc based on role: staff for trainers/owners, members for members.
    const role = claims.role || 'member';
    const path = (role === 'trainer' || role === 'owner')
      ? staffDoc(gymId, user.uid)
      : memberDoc(gymId, user.uid);
    const ref = doc(db, path);
    setDocRef(ref);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) {
        // Doc doesn't exist yet — create it with terms not accepted so the gate shows.
        setAccepted(false);
        return;
      }
      const data = snap.data();
      const version = data.acceptedTermsVersion || 0;
      setAccepted(version >= CURRENT_TERMS_VERSION);
    }, () => setAccepted(true));
    return unsub;
  }, [user, gymId, claims.role]);

  if (accepted === null) return <div className="centered">Loading…</div>;
  if (accepted) return children;
  return <TermsGate docRef={docRef} />;
}

function TermsGate({ docRef }) {
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function accept() {
    setBusy(true);
    setErr(null);
    try {
      await setDoc(docRef, {
        acceptedTermsAt: serverTimestamp(),
        acceptedTermsVersion: CURRENT_TERMS_VERSION,
        // Snapshot the exact text agreed to, so the member's Legal record stays
        // accurate even after the bundled terms are updated to a later version.
        acceptedTermsText: buildLegalTextPlain({ version: CURRENT_TERMS_VERSION }),
        status: 'active',
      }, { merge: true });
    } catch (e) {
      setErr(e.message || 'Failed to save acceptance. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="shell" style={{ maxWidth: 640, paddingTop: 40 }}>
      <img src="/trainforge-logo.svg" alt="" width={56} height={56} style={{ display: 'block', margin: '0 auto 16px' }} />
      <h2 style={{ textAlign: 'center', marginBottom: 4 }}>Welcome to TrainForge</h2>
      <p style={{ textAlign: 'center', color: 'var(--text-dim)', marginBottom: 24 }}>
        Before you start, please review and accept the following.
      </p>

      <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20, maxHeight: 'calc(100vh - 320px)', overflowY: 'auto', marginBottom: 16, fontSize: 13, lineHeight: 1.7, color: 'var(--text-dim)' }}>
        <LegalDocument />
      </div>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          style={{ marginTop: 4, width: 20, height: 20, accentColor: 'var(--accent)' }}
        />
        <span style={{ fontSize: 14 }}>
          I have read and agree to the <b>Terms of Service</b>, <b>Privacy Policy</b>, and <b>Fitness Liability Waiver</b>. I understand that AI-generated programs are suggestions and not professional medical advice.
        </span>
      </label>

      {err && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{err}</div>}
      <button className="primary" onClick={accept} disabled={!checked || busy} style={{ width: '100%' }}>
        {busy ? 'Saving…' : 'Accept & Continue'}
      </button>
    </div>
  );
}

